import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { ClienteEditarDTO } from '../../utilidades/clientes.models';
import { ClientesService } from '../../utilidades/clientes.service';
import { CONFIGURACION_FORMULARIO_CLIENTE } from './utilidades/datos-cliente.static';
import { deepCopy } from 'src/app/compartido/utilidades/objetos.utils';
import {
  ConfiguracionFormulario,
  ValoresFormulario,
} from 'src/app/compartido/formulario/utilidades/formulario.models';
import { AlertasService } from 'src/app/nucleo/utilidades/alertas.service';
import { ErrorFrontend } from 'src/app/compartido/utilidades/error.utils';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FormularioModule } from 'src/app/compartido/formulario/formulario.module';
import { CommonModule } from '@angular/common';
import { Cliente } from '../../utilidades/cliente.class';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AutorizacionService } from 'src/app/nucleo/autorizacion/autorizacion.service';
import { Permiso } from 'src/app/nucleo/autorizacion/autorizacion.models';
import { AutorizacionModule } from 'src/app/nucleo/autorizacion/autorizacion.module';

@Component({
  selector: 'clientes-datos-cliente',
  templateUrl: './datos-cliente.component.html',
  styleUrls: ['./datos-cliente.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    FormularioModule,
    CommonModule,
    MatProgressSpinnerModule,
    AutorizacionModule,
  ],
})
export class DatosClienteComponent implements OnInit {
  /* ID del cliente */
  @Input({ required: true }) public idCliente!: number;

  /* Cliente */
  protected cliente: Cliente | undefined;

  /* Configuración del formulario */
  @Input() public edicion: 'activada' | 'activable' | 'desactivada' =
    'desactivada';

  /* Estado de edición */
  protected editar = signal<boolean>(false);

  /* Estado de carga */
  protected cargando = signal<boolean>(true);

  /* Formulario */
  protected configFormulario: ConfiguracionFormulario = deepCopy(
    CONFIGURACION_FORMULARIO_CLIENTE,
  );
  protected valorFormulario: ValoresFormulario = {};
  protected validezFormulario: boolean = false;

  /* Campos readonly del formulario */
  private _camposReadonly: Set<string> = new Set(
    CONFIGURACION_FORMULARIO_CLIENTE.campos
      .filter((campo) => campo.readonly)
      .map((campo) => campo.slug),
  );

  /* Constantes */
  protected readonly Permiso = Permiso;

  constructor(
    private readonly _autorizacionService: AutorizacionService,
    private readonly _clientesService: ClientesService,
    private readonly _alertasService: AlertasService,
    private readonly _cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.cargando.set(true);

    // Obtener el cliente
    this._clientesService
      .obtenerCliente$(this.idCliente)
      .then((cliente: Cliente) => {
        // Asignar el cliente
        this.cliente = cliente;

        // Configurar el formulario
        const datos = this.cliente as Record<string, any>;
        for (const campo of this.configFormulario.campos) {
          if (campo.slug === 'organizacion')
            campo.valor = datos['organizacion']['nombre'];
          else if (campo.slug === 'tipo_identificacion')
            campo.valor = datos['tipoIdentificacion'];
          else campo.valor = datos[campo.slug];
        }

        this.cargando.set(false);
        this._cdr.markForCheck();
      })
      .catch((error: ErrorFrontend) => {
        this._alertasService.alertarError('Error: ' + error.message);
        this.cargando.set(false);
      });

    // Actualizar el estado de edición
    this._autorizacionService
      .tienePermiso$('cliente', Permiso.EDITAR)
      .then((tienePermiso: boolean) => {
        if (tienePermiso && this.edicion === 'activada') this.activarEdicion();
        else this.desactivarEdicion();
      });
  }

  /**
   * Activa el modo de edición del formulario.
   * @author Juan Corral
   */
  protected activarEdicion(): void {
    this.editar.set(true);
    this.configFormulario.campos.forEach((campo) => {
      // Si el campo no es readonly, lo habilita para edición
      if (!this._camposReadonly.has(campo.slug)) campo.readonly = false;
    });
    this._cdr.markForCheck();
  }

  /**
   * Desactiva el modo de edición del formulario.
   * @author Juan Corral
   */
  protected desactivarEdicion(): void {
    this.editar.set(false);
    this.configFormulario.campos.forEach((campo) => (campo.readonly = true));
    this._cdr.markForCheck();
  }

  /**
   * Guarda los cambios realizados al cliente.
   * @author Juan Corral
   */
  protected guardarCambios(): void {
    if (!this.validezFormulario) return;

    this.cargando.set(true);
    this.desactivarEdicion();

    const valor = this.valorFormulario;
    const datos: ClienteEditarDTO = {
      tipo_identificacion: valor['tipo_identificacion'],
      identificacion: valor['identificacion'],
      nombre: valor['nombre'],
      telefono: valor['telefono'],
      celular: valor['celular'],
      correo: valor['correo'],
    };

    this._clientesService
      .actualizarCliente$(this.idCliente, datos)
      .then(() => {
        this._alertasService.alertarExito('Actualización exitosa');
        this.configFormulario = deepCopy(CONFIGURACION_FORMULARIO_CLIENTE);
        this.cliente = undefined;
        this.ngOnInit();
      })
      .catch((error: ErrorFrontend) => {
        this._alertasService.alertarError('Error: ' + error.message);
        this.cargando.set(false);
      });
  }
}
